import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import type { ShopItem } from '../lib/types';
import { Sheet } from '../components/Sheet';
import { CrystalIcon } from '../components/Currency';
import { nfmt } from '../lib/format';
import { haptic, tg } from '../lib/telegram';
import { useApp } from '../App';

/** Раздел 4.5. Кристаллы не сгорают, поэтому магазин — витрина на несколько сезонов. */

export function ShopScreen() {
  const { me, refresh, toast } = useApp();
  const [items, setItems] = useState<ShopItem[] | null>(null);
  const [confirm, setConfirm] = useState<ShopItem | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get<ShopItem[]>('/shop').then(setItems).catch(() => {});
  useEffect(() => { void load(); }, []);

  async function buy(item: ShopItem) {
    setBusy(true);
    try {
      const r = await api.post<{ fileUrl: string | null }>(`/shop/${item.id}/buy`);
      haptic.ok();
      setConfirm(null);
      toast(r.fileUrl ? 'Куплено — файл в профиле' : 'Куплено');
      await Promise.all([load(), refresh()]);
    } catch (e) {
      haptic.no();
      toast((e as ApiError).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  if (!items) return <div className="h-64 animate-pulse rounded-3xl bg-surface" />;

  return (
    <div className="rise pb-4">
      <div className="mb-5 flex items-center gap-2 text-sm text-ash">
        <CrystalIcon size={16} />
        <span>Кристаллы копятся вместе с коинами и не сгорают в конце сезона.</span>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {items.map((item) => (
          <article key={item.id}
            className={`card flex flex-col overflow-hidden p-3 ${item.is_coming_soon ? 'opacity-70' : ''}`}>
            <div className="mb-2 flex h-20 items-center justify-center rounded-xl border border-line bg-raised">
              {item.image_url
                ? <img src={item.image_url} alt="" className={`h-full w-full rounded-xl object-cover
                    ${item.is_coming_soon ? 'blur-[6px]' : ''}`} loading="lazy" />
                : <CrystalIcon size={28} />}
            </div>
            <h3 className={`text-[13px] font-semibold leading-snug ${item.is_coming_soon ? 'blur-[3px]' : ''}`}>
              {item.title}
            </h3>
            <div className="mt-auto pt-2">
              {item.is_coming_soon ? (
                <button className="btn-ghost w-full px-2 py-2 text-xs"
                  onClick={() => { haptic.tap(); toast('Появится в следующем сезоне'); }}>
                  Скоро
                </button>
              ) : item.owned ? (
                <span className="block py-2 text-center text-xs text-turq">Куплено</span>
              ) : (
                <button
                  className={me.crystals >= item.price_crystals
                    ? 'btn-primary w-full px-2 py-2 text-xs'
                    : 'btn-ghost w-full px-2 py-2 text-xs'}
                  onClick={() => { haptic.tap(); setConfirm(item); }}
                >
                  {nfmt(item.price_crystals)}
                </button>
              )}
            </div>
          </article>
        ))}
      </div>

      <Sheet open={!!confirm} onClose={() => setConfirm(null)} title={confirm?.title}>
        {confirm && (
          <>
            {confirm.description && <p className="mb-3 text-sm text-ash">{confirm.description}</p>}
            <div className="mb-4 flex items-center justify-between rounded-xl border border-line bg-raised px-4 py-3">
              <span className="text-sm text-ash">Спишется</span>
              <span className="flex items-center gap-1.5 font-bold text-violet num">
                <CrystalIcon size={16} />{nfmt(confirm.price_crystals)}
              </span>
            </div>
            {me.crystals < confirm.price_crystals ? (
              <p className="text-center text-sm text-alarm">
                Не хватает {nfmt(confirm.price_crystals - me.crystals)} кристаллов
              </p>
            ) : (
              <button className="btn-primary w-full" disabled={busy} onClick={() => buy(confirm)}>
                {busy ? 'Покупаем…' : 'Купить'}
              </button>
            )}
          </>
        )}
      </Sheet>
    </div>
  );
}

export function openFile(url: string) {
  tg.openLink(url);
}
